export type PageHelpEntry = {
  title: string
  body: string
}

/**
 * In-app help keyed by pathname (and a few synthetic keys such as `ai-assist`).
 * Keep copy short: what the screen is for, then what to do.
 */
export const PAGE_HELP: Record<string, PageHelpEntry> = {
  '/opportunities': {
    title: 'Opportunities',
    body: `### What this is
Your forecast pipeline for the selected **year** and **view** (sidebar). Owners see their lines; finance sees the portfolio.

### What to do
1. Scan the **customer revenue** bars (top accounts by upside; click to filter).
2. Open a row to edit stage, dates, headcount, rates, and rationale.
3. Use filters / search to find lines; export or publish from the toolbar when you have finance rights.
4. Open **AI assist** (sparkles) for proposed updates or Ask questions.`,
  },
  '/forecast': {
    title: 'Monthly forecast',
    body: `### What this is
Month-by-month committed / weighted / upside for the selected year and view.

### What to do
1. Confirm **year** and **view** in the sidebar.
2. Read the monthly strip and totals — this is the rollup finance reviews.
3. Drill back to **Opportunities** to change underlying lines; this page does not edit deals.`,
  },
  '/console': {
    title: 'Finance console',
    body: `### What this is
Finance’s at-a-glance board: hygiene, unanswered alerts, unconfirmed owners, and publish readiness.

### What to do
1. Triage red / amber items — open the linked opportunity or admin screen.
2. Chase owners who have not confirmed their forecast this week.
3. When the book looks clean, publish from the opportunities / publish flow.`,
  },
  '/published': {
    title: 'Published baselines',
    body: `### What this is
Named forecast baselines for the selected year. The grid shows every non-lost opportunity with Jan–Dec amounts, year total, and weighted total. Stage, probability, bill rate, and headcount sit under each opportunity name.

### What to do
1. Publish from Opportunities with a required baseline name (full portfolio — no view picker).
2. Pick a baseline from the top dropdown (defaults to the latest). Filter by customer or opportunity as needed.
3. Finance/Admin: **Export CSV**, **Restore** (reason required), or **Remove**.`,
  },
  '/alerts': {
    title: 'My alerts',
    body: `### What this is
Open alerts on opportunities you own (or can act on): start-date risk, weekly prompts, stalls, and similar.

### What to do
1. Open each alert and respond (acknowledge, revise start, still active, etc.).
2. Unanswered alerts stay visible here and on the finance console until you act.`,
  },
  '/admin': {
    title: 'Admin',
    body: `### What this is
Catalogue and operations settings that shape forecasting, alerts, and access.

### What to do
Pick a tab on the left. Changes here affect everyone — prefer small, reviewed updates.`,
  },
  '/admin/stages': {
    title: 'Stages',
    body: `### What this is
Pipeline stages and win probabilities used for weighted forecast.

### What to do
Add or edit stages carefully — probability drives weighted revenue. Retire unused stages instead of deleting history.`,
  },
  '/admin/views': {
    title: 'Forecast views',
    body: `### What this is
Named views (for example probability-weighted vs unweighted) available in the sidebar.

### What to do
Keep one clear default. Toggle weighting to match how leadership wants to read the book.`,
  },
  '/admin/years': {
    title: 'Forecast years',
    body: `### What this is
Which calendar / forecast years are open for editing.

### What to do
Open the year owners should work in; close years you no longer want edited.`,
  },
  '/admin/drivers': {
    title: 'Forecast drivers',
    body: `### What this is
Lookup of forecast-driver codes owners pick on each opportunity.

### What to do
Keep labels clear and retire obsolete drivers so the list stays short.`,
  },
  '/admin/custom-fields': {
    title: 'Custom fields',
    body: `### What this is
Extra opportunity fields beyond the core model.

### What to do
Add fields only when the standard form is insufficient. Changing types later can break existing data.`,
  },
  '/admin/revenue-rules': {
    title: 'Revenue rules',
    body: `### What this is
How headcount, rates, and patterns turn into monthly revenue.

### What to do
Preview before activate. Use effective dates so you do not rewrite closed history by accident.`,
  },
  '/admin/hygiene': {
    title: 'Hygiene checks',
    body: `### What this is
Configurable quality rules that flag messy or incomplete opportunities.

### What to do
Enable checks that match your process; review hits on the finance console and opportunity list.`,
  },
  '/admin/alerts': {
    title: 'Alert rules',
    body: `### What this is
When the system creates owner alerts (start date, weekly confirm, stall, and so on).

### What to do
Tune thresholds and enablement. Owners answer alerts on **Alerts**; finance watches the console.`,
  },
  '/admin/closed-months': {
    title: 'Closed months',
    body: `### What this is
Months locked so owners cannot rewrite past forecast.

### What to do
Close months after the book is agreed. Reopen only when you intentionally need a correction.`,
  },
  '/admin/config-transfer': {
    title: 'Config transfer',
    body: `### What this is
Export / import of catalogue configuration between environments.

### What to do
Export from a known-good env, review the file, then import carefully into the target.`,
  },
  '/admin/access-log': {
    title: 'Access log',
    body: `### What this is
Audit of sign-ins and notable access events.

### What to do
Use for security / compliance review — this screen is read-only.`,
  },
  '/admin/ai-log': {
    title: 'AI turn log',
    body: `### What this is
History of AI Assist turns and model interactions for support and audit.

### What to do
Inspect failed or surprising turns; adjust AI settings on **System** if needed.`,
  },
  '/admin/customers': {
    title: 'Customers',
    body: `### What this is
Customer master used by opportunities.

### What to do
Create or rename customers carefully — opportunities link by customer id.`,
  },
  '/admin/users': {
    title: 'Access list',
    body: `### What this is
Who can sign in and which role they have (owner, finance, admin, viewer, …).

### What to do
Grant the least privilege needed. Owners only see their opportunities; finance sees the portfolio.`,
  },
  '/admin/email-failures': {
    title: 'Email failures',
    body: `### What this is
Outbound mail that ACS / the mailer could not deliver.

### What to do
Fix sender / domain / recipient issues, then retry or notify the user another way.`,
  },
  '/admin/system': {
    title: 'System',
    body: `### What this is
Technical settings: currency, timezone, email sender, AI kill switch / models, feature switches, and chart options such as customer bar top N.

### What to do
Change with care — these settings affect the whole tenant. Save each section after editing.`,
  },
  'ai-assist': {
    title: 'AI assist',
    body: `### What this is
Side panel for **Update** (propose opportunity changes) and **Ask** (read-only questions).

### What to do
1. **Update** — describe a concrete change or paste an email, review cards, then Confirm.
2. **Ask** — use a chip or freeform (if enabled); follow-ups keep session context.
3. Drag the header to move the panel. Clear chat starts a fresh session.`,
  },
}

/** Longest-prefix match so \`/admin/stages/...\` still resolves if paths deepen later. */
export function resolvePageHelp(pathname: string): PageHelpEntry | null {
  const normalized = (pathname.replace(/\/+$/, '') || '/').trim()
  if (PAGE_HELP[normalized]) return PAGE_HELP[normalized]

  const keys = Object.keys(PAGE_HELP)
    .filter((key) => key.startsWith('/') && normalized.startsWith(key))
    .sort((a, b) => b.length - a.length)
  const hit = keys[0]
  return hit ? (PAGE_HELP[hit] ?? null) : null
}
